import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion, useDragControls, useReducedMotion } from 'framer-motion';
import { ArrowsIn, ArrowsOut, At, ClockCounterClockwise, Plus, Trash, X } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { useChat, type ChatMessage } from '@/contexts/ChatContext';
import { RUTA_AI_NARROW_QUERY, useRutaAi, type RutaAiSeed } from '@/contexts/RutaAiContext';
import {
  detachedRutaAiAllowsDrag,
  detachedRutaAiFillsViewport,
  detachedRutaAiPanelClass,
  detachedRutaAiShellClass,
} from '@/lib/rutaAiPanelLayout';
import { framingCopy } from '@/lib/assistantFraming';
import { getIdToken } from '@/lib/api/auth';
import { getApiUrl, isLocalApi } from '@/lib/api/core';
import { AssistantGlyph } from '@/components/ai-elements/assistant-brand';
import { AiCreditsMeter } from '@/components/ai-elements/ai-credits-meter';
import { AssistantMentionPicker } from '@/components/ai-elements/assistant-mention-picker';
import { EntityType, applyAssistantEvent, emptyAssistantTurn, filedFeedbackFromToolData, replayRequestBody, type AssistantReplay, type FiledFeedback } from '@ruta/shared';
import { ReportReceivedDialog } from '@/components/feedback/ReportReceivedDialog';
import { resolveEntityRoute } from '@/lib/entityRoutes';
import { toReference, type MentionOption, type AssistantEntityReference } from '@/lib/api/assistantMentions';
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from '@/components/ai-elements/conversation';
import { AssistantMessageBubble } from '@/components/ai-elements/assistant-message-bubble';
import {
  PromptInput,
  PromptInputFooter,
  PromptInputSubmit,
  PromptInputTextarea,
  type PromptInputMessage,
} from '@/components/ai-elements/prompt-input';
import { PopoverContent } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

/**
 * An icon-only header control that says what it does on hover. The trigger is a
 * wrapper span, not the button, so the label still shows while the button is
 * disabled (a disabled button gets no pointer events; RUT-3793).
 */
function HeaderControl({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">{children}</span>
        </TooltipTrigger>
        <TooltipContent side="bottom">{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
import { useOrganization } from '@/hooks/useQueries';
import { queryKeys } from '@/lib/queryKeys';
import { assistantErrorCopy, closeAssistantStream, finalizeAssistantTurn } from '@/lib/assistantStreamTurn';

const ASSISTANT_STREAM_URL = import.meta.env.VITE_ASSISTANT_CHAT_STREAM_URL;
const getAssistantChatUrl = () =>
  isLocalApi
    ? `${getApiUrl()}/assistant/chat`
    : ASSISTANT_STREAM_URL || `${getApiUrl()}/assistant/chat`;

function relativeDate(date: Date): string {
  const diffMs = Date.now() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return `${Math.floor(diffDays / 30)}mo ago`;
}

// Global Ruta AI surface. Opens as a popover mounted under the header sparkle;
// the user can detach into a free-floating draggable panel (the previous default).
// The app stays interactive in both modes.
export function RutaAiPopover() {
  const reduce = useReducedMotion();
  const navigate = useNavigate();
  const { open, mode, seed, applyDraft, framing, pageContext, closeAi, clearSeed, detachAi, dockAi } =
    useRutaAi();
  const starter = framingCopy(framing);
  const queryClient = useQueryClient();
  const { data: orgData } = useOrganization({ enabled: open });
  const aiCredits = orgData?.usage?.aiCredits;
  const {
    messages,
    addMessage,
    updateMessage,
    startNewConversation,
    conversations,
    loadConversation,
    deleteConversation,
    refreshConversations,
    currentConversationId,
  } = useChat();
  const [isGenerating, setIsGenerating] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [filedFeedback, setFiledFeedback] = useState<FiledFeedback | null>(null);

  // Fresh credit totals whenever the panel opens (usage is metered server-side
  // after each reply; the org profile is the source of truth for the gauge).
  useEffect(() => {
    if (!open) return;
    void queryClient.invalidateQueries({ queryKey: queryKeys.organizations.current() });
  }, [open, queryClient]);
  // @-mention typeahead (mirrors mobile): a `/@(\w*)$/` match at the caret opens
  // a tabbed picker filtered by the typed text; selecting strips the "@query" and
  // pins an entity reference that rides along as `references` on the next message.
  const [draft, setDraft] = useState('');
  const draftRef = useRef('');
  const cursorRef = useRef(0);
  const mentionPosRef = useRef(0);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [pendingRefs, setPendingRefs] = useState<MentionOption[]>([]);
  const pendingRefsRef = useRef<MentionOption[]>([]);
  pendingRefsRef.current = pendingRefs;
  const textareaElRef = useRef<HTMLTextAreaElement | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const submitLockRef = useRef(false);
  // Flagged just before a seeded submit so that submit starts a fresh conversation.
  const freshStartRef = useRef(false);
  // References for the next seeded submit (e.g. the flagged visit), passed out of
  // band because PromptInput's onSubmit owns handleSubmit's argument list.
  const seededRefsRef = useRef<MentionOption[] | null>(null);
  // Short label for a writing-help seed — shown in the bubble instead of the brief.
  const seededDisplayRef = useRef<string | null>(null);
  // Guarantees each seed is turned into messages exactly once, so the warmed-up
  // reply and a live send can never both render (deterministic single answer).
  const handledSeedRef = useRef<RutaAiSeed | null>(null);
  const dragControls = useDragControls();
  const constraintsRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [fillsViewport, setFillsViewport] = useState(() => detachedRutaAiFillsViewport());
  const allowDrag = mode === 'detached' && detachedRutaAiAllowsDrag(fillsViewport);

  useEffect(() => {
    const update = () => setFillsViewport(detachedRutaAiFillsViewport());
    update();
    window.addEventListener('resize', update);
    window.visualViewport?.addEventListener('resize', update);
    const mq = typeof window.matchMedia === 'function' ? window.matchMedia(RUTA_AI_NARROW_QUERY) : null;
    mq?.addEventListener('change', update);
    return () => {
      window.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('resize', update);
      mq?.removeEventListener('change', update);
    };
  }, []);

  // Outside-click dismiss only for the free-floating panel. Anchored popover mode
  // is owned by Radix (onOpenChange in RutaAiProvider).
  useEffect(() => {
    if (!open || mode !== 'detached') return;
    const onPointerDown = (e: PointerEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        closeAi();
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, mode, closeAi]);

  const stopGeneration = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setIsGenerating(false);
  }, []);

  const handleNewConversation = useCallback(() => {
    stopGeneration();
    startNewConversation();
  }, [stopGeneration, startNewConversation]);

  const handleLoadConversation = useCallback((id: string) => {
    stopGeneration();
    loadConversation(id);
    setShowHistory(false);
  }, [stopGeneration, loadConversation]);

  const setDraftValue = (value: string) => {
    draftRef.current = value;
    setDraft(value);
  };

  const updateMention = (text: string, cursor: number) => {
    cursorRef.current = cursor;
    const match = text.slice(0, cursor).match(/@(\w*)$/);
    if (match) {
      mentionPosRef.current = cursor - match[0].length;
      setMentionQuery(match[1]);
    } else {
      setMentionQuery(null);
    }
  };

  const handleDraftChange = (value: string, cursor: number) => {
    setDraftValue(value);
    updateMention(value, cursor);
  };

  const insertMention = (option: MentionOption) => {
    const text = draftRef.current;
    const before = text.slice(0, mentionPosRef.current);
    const after = text.slice(cursorRef.current);
    setDraftValue(before + after);
    cursorRef.current = before.length;
    setPendingRefs((prev) =>
      prev.some((r) => r.entityType === option.entityType && r.entityId === option.entityId)
        ? prev
        : [...prev, option],
    );
    setMentionQuery(null);
    textareaElRef.current?.focus();
  };

  const openMentionPicker = () => {
    const next = `${draftRef.current}@`;
    setDraftValue(next);
    mentionPosRef.current = next.length - 1;
    cursorRef.current = next.length;
    setMentionQuery('');
    textareaElRef.current?.focus();
  };

  // Tapping an @-reference chip on a sent message jumps to that entity. The
  // popover stays open (it's a non-modal overlay), so a still-streaming answer
  // keeps working while you land on the item.
  const handleReferencePress = useCallback(
    (ref: AssistantEntityReference) => {
      let route: string | null = null;
      if (ref.entityType === EntityType.VISIT) {
        route = resolveEntityRoute({ type: 'VISIT', params: { id: ref.entityId } });
      } else if (ref.entityType === EntityType.SUBSCRIPTION && ref.customerId) {
        route = resolveEntityRoute({
          type: 'SUBSCRIPTION',
          params: { customerId: ref.customerId, subscriptionId: ref.entityId },
        });
      } else if (ref.entityType === EntityType.CUSTOMER) {
        route = resolveEntityRoute({ type: 'CUSTOMER', params: { id: ref.entityId } });
      } else if (ref.entityType === EntityType.SERVICE_TEMPLATE) {
        route = resolveEntityRoute({ type: 'SERVICE_TEMPLATE', params: { id: ref.entityId } });
      }
      if (route) navigate(route);
    },
    [navigate],
  );

  const handleSubmit = useCallback(
    async (promptMessage: PromptInputMessage, replay?: AssistantReplay) => {
      const text = (promptMessage.text ?? '').trim();
      // Read+clear the fresh-start flag and any seeded references FIRST so an
      // empty/aborted submit can't strand them and corrupt the NEXT message.
      const freshStart = freshStartRef.current;
      freshStartRef.current = false;
      const seededRefs = seededRefsRef.current;
      seededRefsRef.current = null;
      const displayContent = seededDisplayRef.current ?? undefined;
      seededDisplayRef.current = null;
      if (!text || isGenerating || submitLockRef.current) return;
      submitLockRef.current = true;

      // Snapshot @-mentions for this message, then reset the composer. A seeded
      // send (e.g. the flagged-visit explanation) supplies its own references.
      const refs = seededRefs ?? pendingRefsRef.current;
      setPendingRefs([]);
      setMentionQuery(null);
      setDraftValue('');

      const userMsg: ChatMessage = {
        id: Date.now().toString(),
        role: 'user',
        content: text,
        ...(displayContent ? { displayContent } : {}),
        references: refs.length ? refs.map(toReference) : undefined,
        createdAt: new Date(),
      };
      addMessage(userMsg);
      const assistantMsgId = (Date.now() + 1).toString();
      addMessage({
        id: assistantMsgId,
        role: 'assistant',
        content: '',
        activityLabel: 'Reading your question',
        createdAt: new Date(),
      });
      setIsGenerating(true);

      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const token = getIdToken();
        if (!token) throw new Error('Please log in again to use Ruta AI.');

        const allMessages = freshStart ? [userMsg] : [...messages, userMsg];
        const res = await fetch(getAssistantChatUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            messages: allMessages.map((m) => ({ role: m.role, content: m.content })),
            references: refs.length ? refs.map(toReference) : undefined,
            client: 'web',
            framing,
            ...(currentConversationId ? { conversation_id: currentConversationId } : {}),
            ...(replay ? { replay: replayRequestBody(replay) } : {}),
            ...(pageContext ? { pageContext } : {}),
          }),
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`Ruta AI is unavailable right now (HTTP ${res.status}).`);
        if (!res.body) throw new Error('Ruta AI is unavailable right now (empty response).');

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let view = emptyAssistantTurn();
        let pendingTools: Record<string, { name: string; input: Record<string, unknown> }> = {};
        let didExecuteMutation = false;
        let filedThisTurn: FiledFeedback | null = null;

        const paint = () => {
          updateMessage(assistantMsgId, {
            content: view.text,
            activityLabel: view.showActivity ? view.activityLabel : null,
            suggestions: view.suggestions.length ? view.suggestions : undefined,
            navigationActions: view.navigationActions.length
              ? view.navigationActions.map((action) => ({ ...action, state: action.state ?? {} }))
              : undefined,
            blocks: view.blocks.length ? view.blocks : undefined,
            pendingConfirmation: view.pendingReplay && !view.pendingRetry ? true : undefined,
            pendingRetry: view.pendingRetry || undefined,
            pendingReplay: view.pendingReplay ?? undefined,
            confirmationPrompt: view.pendingReplay && !view.pendingRetry ? view.confirmationPrompt : undefined,
            toolNames: view.toolNames.length ? view.toolNames : undefined,
            toolCalls: view.toolCalls,
          });
        };

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          const events = buffer.split('\n\n');
          buffer = events.pop() || '';
          for (const event of events) {
            if (!event.startsWith('data: ')) continue;
            try {
              const parsed = JSON.parse(event.slice(6));
              if (parsed.type === 'tool_call' && parsed.input?.confirmed === true) {
                didExecuteMutation = true;
              }
              if (parsed.type === 'tool_result') {
                const filed = filedFeedbackFromToolData(parsed.result?.data);
                if (filed) filedThisTurn = filed;
              }
              if (parsed.type === 'error' && typeof parsed.message === 'string') {
                parsed.message = assistantErrorCopy(parsed.message);
              }
              const applied = applyAssistantEvent(view, parsed, pendingTools);
              view = applied.view;
              pendingTools = applied.pendingTools;
              paint();
              if (view.done) {
                submitLockRef.current = false;
                setIsGenerating(false);
              }
            } catch {
              // ignore parse errors
            }
          }
        }

        if (!view.done) {
          view = closeAssistantStream(view);
          paint();
        }

        // A confirmed AI action changed data — refresh now, and again after a beat
        // for actions written asynchronously (e.g. customer message campaigns).
        if (didExecuteMutation) {
          queryClient.invalidateQueries();
          setTimeout(() => queryClient.invalidateQueries(), 3000);
        }

        const turn = finalizeAssistantTurn({ assistantText: view.text, streamError: view.streamError });
        const hasCards = view.blocks.length > 0 || view.navigationActions.length > 0 || !!view.pendingReplay;
        if (turn.failed && !hasCards) {
          updateMessage(assistantMsgId, {
            content: turn.content,
            failed: true,
            activityLabel: null,
            suggestions: undefined,
            navigationActions: undefined,
            blocks: undefined,
            pendingConfirmation: undefined,
            pendingRetry: undefined,
            pendingReplay: undefined,
            confirmationPrompt: undefined,
            toolCalls: undefined,
            toolNames: undefined,
          });
        } else {
          updateMessage(assistantMsgId, {
            content: turn.failed ? '' : turn.content,
            activityLabel: null,
            suggestions: view.suggestions.length ? view.suggestions : undefined,
            navigationActions: view.navigationActions.length
              ? view.navigationActions.map((action) => ({ ...action, state: action.state ?? {} }))
              : undefined,
            blocks: view.blocks.length ? view.blocks : undefined,
            pendingConfirmation: view.pendingReplay && !view.pendingRetry ? true : undefined,
            pendingRetry: view.pendingRetry || undefined,
            pendingReplay: view.pendingReplay ?? undefined,
            confirmationPrompt: view.pendingReplay && !view.pendingRetry ? view.confirmationPrompt : undefined,
            toolNames: view.toolNames,
            toolCalls: view.toolCalls ?? view.toolNames.length,
          });
        }

        // Self-navigate on an explicit "take me there" (navigate_to sets autoNavigate);
        // get_knowledge's button stays a manual tap-to-go affordance.
        const autoNav = view.navigationActions.find((a) => a.autoNavigate);
        if (autoNav) {
          navigate(autoNav.path, { state: autoNav.state });
          closeAi();
        }
        if (filedThisTurn) setFiledFeedback(filedThisTurn);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          updateMessage(assistantMsgId, { activityLabel: null });
          return;
        }
        const content =
          err instanceof TypeError
            ? 'Network error. Check your connection and try again.'
            : assistantErrorCopy(err instanceof Error ? err.message : null);
        updateMessage(assistantMsgId, {
          content,
          failed: true,
          activityLabel: null,
        });
      } finally {
        submitLockRef.current = false;
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
          setIsGenerating(false);
        }
        // Credit meter rolls after each turn once the server has recorded usage.
        void queryClient.invalidateQueries({ queryKey: queryKeys.organizations.current() });
      }
    },
    [messages, isGenerating, addMessage, updateMessage, queryClient, navigate, closeAi, pageContext, framing, currentConversationId],
  );

  // Opened with a seed (e.g. the flagged-visit "Explain this review" bubble):
  // start a fresh conversation and auto-submit the seeded prompt, once.
  useEffect(() => {
    if (!open || !seed) return;
    // Process each seed exactly once. This effect re-runs whenever handleSubmit's
    // identity changes (it closes over messages/isGenerating); without this guard
    // a single open could fire multiple sends. Keyed by seed object identity —
    // every openAi() call mints a fresh seed, so re-explaining still works.
    if (handledSeedRef.current === seed) return;
    handledSeedRef.current = seed;
    const prompt = seed.prompt;
    const seedRefs = seed.references;
    // A seed with no prompt is malformed — bail before it wipes a loaded
    // conversation (startNewConversation) or crashes handleSubmit on undefined text.
    if (!prompt || !prompt.trim()) {
      clearSeed();
      return;
    }
    clearSeed();
    startNewConversation();
    freshStartRef.current = true;
    seededRefsRef.current = seedRefs ?? null;
    seededDisplayRef.current = seed.displayPrompt?.trim() || null;
    void handleSubmit({ text: prompt, files: [] });
  }, [open, seed, clearSeed, startNewConversation, handleSubmit]);

  const hasMessages = messages.length > 0 || isGenerating;
  const isDetached = mode === 'detached';

  // One panel — same markup, chrome, and size whether anchored or released.
  // Only the mount changes: PopoverContent positions it under the sparkle; detach
  // drops the popover constraint so the same panel free-floats and can drag.
  //
  // When docked, open with opacity only — never y/scale. Radix/Floating UI already
  // positions the shell with transform; a second transform on this node recomposes
  // subpixel and reads as a 1–2px diagonal wobble.
  const panel = (
    <motion.div
      key="panel"
      ref={panelRef}
      data-testid="ruta-ai-panel"
      data-placement={isDetached ? (fillsViewport ? 'viewport-fill' : 'float-end') : 'popover'}
      drag={allowDrag}
      dragControls={dragControls}
      dragListener={false}
      dragConstraints={allowDrag ? constraintsRef : undefined}
      dragMomentum={false}
      initial={
        reduce
          ? false
          : allowDrag
            ? { opacity: 0, y: -24, scale: 0.98 }
            : { opacity: 0 }
      }
      animate={
        allowDrag
          ? { opacity: 1, y: 0, scale: 1 }
          : { opacity: 1 }
      }
      exit={
        reduce
          ? undefined
          : allowDrag
            ? { opacity: 0, y: -24, scale: 0.98 }
            : { opacity: 0 }
      }
      transition={{ duration: 0.24, ease: [0.2, 0.7, 0.3, 1] }}
      className={cn(
        'flex flex-col overflow-hidden',
        'rounded-[14px] border border-[rgba(134,184,134,0.25)]',
        'shadow-[0_24px_60px_rgba(0,0,0,0.5),0_0_40px_rgba(72,105,72,0.18)]',
        isDetached
          ? detachedRutaAiPanelClass(fillsViewport)
          : 'w-[400px] h-[calc(100vh-1.5rem)] max-h-[760px] max-w-[calc(100vw-1.5rem)]',
      )}
      style={{
        background: 'rgba(10,26,10,0.92)',
        backdropFilter: 'blur(16px) saturate(160%)',
        WebkitBackdropFilter: 'blur(16px) saturate(160%)',
        // Pin x so Framer Motion cannot write a measured negative translate
        // (the off-screen-left bounce when drag inits before layout).
        ...(fillsViewport || !allowDrag ? { x: 0 } : {}),
      }}
      role="dialog"
      aria-label="Ruta AI"
    >
      {/* header is the drag handle when released */}
      <header
        onPointerDown={allowDrag ? (e) => dragControls.start(e) : undefined}
        className={cn(
          'relative flex items-center justify-between px-3 pt-3.5 pb-3 border-b border-white/[0.06] shrink-0 select-none',
          allowDrag && 'cursor-grab active:cursor-grabbing touch-none',
        )}
        style={{
          background: 'linear-gradient(180deg, rgba(72,105,72,0.18) 0%, transparent 100%)',
        }}
      >
        {/* History toggle — left */}
        <HeaderControl label={showHistory ? 'Back to chat' : 'View history'}>
          <button
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() =>
              setShowHistory((v) => {
                const next = !v;
                if (next) refreshConversations();
                return next;
              })
            }
            aria-label={showHistory ? 'Back to chat' : 'View history'}
            className={cn(
              'p-1.5 rounded-md transition-colors',
              showHistory
                ? 'text-white bg-white/10'
                : 'text-white/55 hover:text-white hover:bg-white/10',
            )}
          >
            <ClockCounterClockwise className="h-[22px] w-[22px]" />
          </button>
        </HeaderControl>

        {/* Centered title with sparkle glyph (matches mobile) */}
        <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center">
          <div className="flex items-center gap-1.5">
            <AssistantGlyph size={16} />
            <span className="text-[14px] font-semibold text-white leading-tight">
              {showHistory ? 'Chat history' : 'Ruta AI'}
            </span>
            {!showHistory && (
              <span className="text-[9px] font-semibold uppercase tracking-wide text-[#c7cdd8]/90">
                beta
              </span>
            )}
          </div>
          <div className="text-[10px] text-white/50 leading-tight mt-0.5">
            {showHistory
              ? `${conversations.length} conversation${conversations.length === 1 ? '' : 's'}`
              : 'Connected to today’s data'}
          </div>
        </div>

        <div className="flex items-center gap-1" onPointerDown={(e) => e.stopPropagation()}>
          {!showHistory && (
            <HeaderControl label="New conversation">
              <button
                onClick={handleNewConversation}
                disabled={!hasMessages}
                aria-label="New conversation"
                className="p-1.5 rounded-md text-white/55 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-30 disabled:pointer-events-none"
              >
                <Plus className="h-[22px] w-[22px]" />
              </button>
            </HeaderControl>
          )}
          {isDetached ? (
            <HeaderControl label="Dock to header">
              <button
                onClick={dockAi}
                aria-label="Dock Ruta AI to the header"
                className="p-1.5 rounded-md text-white/55 hover:text-white hover:bg-white/10 transition-colors"
              >
                <ArrowsIn className="h-[20px] w-[20px]" />
              </button>
            </HeaderControl>
          ) : (
            <HeaderControl label="Pop out into its own window">
              <button
                onClick={detachAi}
                aria-label="Detach Ruta AI"
                className="p-1.5 rounded-md text-white/55 hover:text-white hover:bg-white/10 transition-colors"
              >
                <ArrowsOut className="h-[20px] w-[20px]" />
              </button>
            </HeaderControl>
          )}
          <HeaderControl label="Close Ruta AI">
            <button
              onClick={closeAi}
              aria-label="Close Ruta AI"
              className="p-1.5 rounded-md text-white/55 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X className="h-[22px] w-[22px]" />
            </button>
          </HeaderControl>
        </div>
      </header>

      {/* Body */}
      <AnimatePresence mode="wait" initial={false}>
        {showHistory ? (
          <motion.div
            key="history"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            transition={{ duration: 0.15 }}
            className="flex-1 min-h-0 overflow-y-auto px-2 py-2"
          >
            <button
              onClick={() => {
                handleNewConversation();
                setShowHistory(false);
              }}
              className="w-full flex items-center gap-2 px-3 py-2.5 mb-2 rounded-lg border border-white/[0.1] bg-white/[0.04] hover:bg-white/[0.08] transition-colors text-[12.5px] text-white/70 hover:text-white"
            >
              <Plus className="h-3.5 w-3.5 shrink-0" />
              New conversation
            </button>
            {conversations.length === 0 ? (
              <div className="text-center text-white/35 text-[12px] py-10">
                No conversations yet
              </div>
            ) : (
              conversations.map((c) => (
                <div
                  key={c.id}
                  className="group flex items-start gap-2 px-3 py-2.5 rounded-lg hover:bg-white/[0.06] cursor-pointer border border-transparent hover:border-white/[0.08] transition-all"
                  onClick={() => handleLoadConversation(c.id)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] text-white/85 truncate leading-snug">
                      {c.title}
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="text-[10.5px] text-white/35">
                        {relativeDate(c.updatedAt)}
                      </span>
                      {c.source && (
                        <span className="text-[9.5px] leading-none px-1.5 py-0.5 rounded-full bg-white/[0.08] text-white/50 border border-white/[0.08]">
                          {c.source === 'mobile' ? 'Crew App' : 'Webapp'}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteConversation(c.id);
                    }}
                    aria-label="Delete conversation"
                    className="opacity-0 group-hover:opacity-100 shrink-0 p-1 rounded text-white/35 hover:text-white/70 transition-all mt-0.5"
                  >
                    <Trash className="h-3 w-3" />
                  </button>
                </div>
              ))
            )}
          </motion.div>
        ) : (
          <motion.div
            key="chat"
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 10 }}
            transition={{ duration: 0.15 }}
            className="flex-1 min-h-0 flex flex-col"
          >
            {hasMessages ? (
              <Conversation className="flex-1 min-h-0">
                <ConversationContent className="px-3 py-3 [&_.is-assistant_*]:!text-white/85">
                  {messages.map((m) => (
                    <AssistantMessageBubble
                      key={m.id}
                      message={m}
                      isGenerating={isGenerating}
                      onReferencePress={handleReferencePress}
                      onApplyDraft={
                        applyDraft && m.role === 'assistant'
                          ? (text) => {
                              applyDraft.onApply(text);
                              closeAi();
                            }
                          : undefined
                      }
                      applyDraftLabel={applyDraft?.label}
                      onNavigate={(a) => {
                        navigate(a.path, { state: a.state });
                        closeAi();
                      }}
                      onLinkNavigate={(path) => {
                        // A hot-linked "/customers?paymentStatus=issues" should land
                        // pre-filtered — the customers page applies filters passed via
                        // navigation state. Detail paths (/customers/ID?tab=billing) keep
                        // their query string and navigate as-is.
                        const qIndex = path.indexOf('?');
                        const pathname = qIndex >= 0 ? path.slice(0, qIndex) : path;
                        const query = qIndex >= 0 ? path.slice(qIndex + 1) : '';
                        // Guard: a /customers/{id} deep-link must carry a customer UUID.
                        // If the assistant used the wrong id (e.g. a subscription ULID),
                        // fall back to the customers list rather than a broken page.
                        const customerDetail = pathname.match(/^\/customers\/([^/]+)$/);
                        const isUuid = (s: string) =>
                          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
                        if (customerDetail && !isUuid(customerDetail[1])) {
                          navigate('/customers');
                        } else if (pathname === '/customers' && query) {
                          const params = new URLSearchParams(query);
                          const filters: Record<string, string> = {};
                          for (const key of ['paymentStatus', 'balanceFilter', 'status']) {
                            const v = params.get(key);
                            if (v) filters[key] = v;
                          }
                          navigate('/customers', Object.keys(filters).length ? { state: { filters } } : undefined);
                        } else {
                          navigate(path);
                        }
                        closeAi();
                      }}
                      onSuggest={(prompt) => handleSubmit({ text: prompt, files: [] })}
                      onConfirm={(msg) => {
                        const pending = msg.pendingReplay;
                        updateMessage(msg.id, {
                          pendingConfirmation: undefined,
                          pendingRetry: undefined,
                          confirmationPrompt: undefined,
                          pendingReplay: undefined,
                        });
                        if (!pending) return;
                        void handleSubmit({ text: msg.pendingRetry ? 'Retry' : 'Confirm', files: [] }, pending);
                      }}
                      onCancel={(msg) => {
                        updateMessage(msg.id, {
                          pendingConfirmation: undefined,
                          pendingRetry: undefined,
                          confirmationPrompt: undefined,
                          pendingReplay: undefined,
                        });
                      }}
                    />
                  ))}

                </ConversationContent>
                <ConversationScrollButton className="bg-[#2a3d2a] border-white/15 text-white hover:bg-[#2a3d2a] hover:text-stone-400 [&_svg]:text-white hover:[&_svg]:text-stone-400" />
              </Conversation>
            ) : (
              <div className="flex-1 min-h-0 overflow-y-auto px-4 py-5">
                <div className="text-[15px] text-white/90 leading-snug font-medium">
                  {starter.greeting}
                </div>
                {starter.chips.length > 0 && (
                  <div className="flex flex-col gap-1.5 mt-4">
                    {starter.chips.map((s) => (
                      <button
                        key={s}
                        onClick={() => handleSubmit({ text: s, files: [] })}
                        className="text-left rounded-md border border-white/[0.08] bg-white/[0.03] hover:bg-white/[0.07] hover:border-white/[0.16] px-3 py-2 text-[12.5px] text-white/85 transition-colors"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
                {framing === 'feedback' && pageContext?.pagePath && (
                  <p className="mt-4 text-[11px] text-white/35">Page: {pageContext.pagePath}</p>
                )}
              </div>
            )}

            {/* Prompt input */}
            <div className="relative shrink-0 border-t border-white/[0.06] p-2.5">
              {mentionQuery !== null && (
                <>
                  <div className="fixed inset-0 z-0" onClick={() => setMentionQuery(null)} />
                  <AssistantMentionPicker
                    query={mentionQuery}
                    onSelect={insertMention}
                    className="absolute left-2.5 right-2.5 bottom-full mb-2 z-10"
                  />
                </>
              )}

              {pendingRefs.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {pendingRefs.map((r) => (
                    <span
                      key={`${r.entityType}:${r.entityId}`}
                      className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-white/[0.08] border border-white/[0.12] text-[11px] text-white/80"
                    >
                      <At className="h-3 w-3 text-white/45" />
                      <span className="max-w-[160px] truncate">{r.displayName}</span>
                      <button
                        type="button"
                        onClick={() =>
                          setPendingRefs((prev) =>
                            prev.filter(
                              (p) =>
                                !(p.entityType === r.entityType && p.entityId === r.entityId),
                            ),
                          )
                        }
                        aria-label={`Remove ${r.displayName}`}
                        className="p-0.5 rounded-full text-white/40 hover:text-white hover:bg-white/10 transition-colors"
                      >
                        <X className="h-2.5 w-2.5" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <PromptInput
                onSubmit={handleSubmit}
                className="bg-white/[0.04] border-white/[0.10] rounded-xl text-stone-900"
              >
                <PromptInputTextarea
                  value={draft}
                  placeholder={starter.placeholder}
                  disabled={isGenerating}
                  className="text-stone-900 placeholder:text-stone-400"
                  onChange={(e) => {
                    textareaElRef.current = e.currentTarget;
                    handleDraftChange(
                      e.currentTarget.value,
                      e.currentTarget.selectionStart ?? e.currentTarget.value.length,
                    );
                  }}
                  onKeyUp={(e) =>
                    updateMention(e.currentTarget.value, e.currentTarget.selectionStart ?? 0)
                  }
                  onClick={(e) =>
                    updateMention(
                      e.currentTarget.value,
                      e.currentTarget.selectionStart ?? 0,
                    )
                  }
                />
                <PromptInputFooter>
                  <button
                    type="button"
                    onClick={openMentionPicker}
                    aria-label="Attach a visit, service, or customer"
                    className={cn(
                      'inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors',
                      mentionQuery !== null
                        ? 'bg-primary/20 text-primary'
                        : 'text-stone-400 hover:text-stone-600 hover:bg-stone-100',
                    )}
                  >
                    <At className="h-4 w-4" />
                  </button>
                  <PromptInputSubmit
                    status={isGenerating ? 'streaming' : undefined}
                    type={isGenerating ? 'button' : 'submit'}
                    onClick={isGenerating ? stopGeneration : undefined}
                  />
                </PromptInputFooter>
              </PromptInput>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Credit totals under the composer; filament lines the panel bottom edge. */}
      <AiCreditsMeter meter={aiCredits} isGenerating={isGenerating} />
    </motion.div>
  );

  const receivedModal = filedFeedback ? (
    <ReportReceivedDialog
      key={filedFeedback.issueIdentifier}
      open
      onOpenChange={(next) => {
        if (!next) setFiledFeedback(null);
      }}
      issueIdentifier={filedFeedback.issueIdentifier}
      category={filedFeedback.category}
    />
  ) : null;

  if (!open) return receivedModal;

  // Anchored: same panel, positioned under the header sparkle. PopoverContent is
  // only a transparent positioning shell — zero chrome of its own.
  //
  // avoidCollisions is OFF on purpose: this panel is nearly viewport-tall, and
  // Floating UI's collision middleware will keep nudging x/y by a pixel as the
  // available box micro-changes (scrollbar, subpixel, anchor glow) — the subtle
  // diagonal wobble. Pin it under the sparkle and let it clip if needed.
  if (!isDetached) {
    return (
      <>
        {receivedModal}
        <PopoverContent
          align="end"
          side="bottom"
          sideOffset={8}
          avoidCollisions={false}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          className="w-auto h-auto max-w-none p-0 border-0 bg-transparent shadow-none outline-none data-[state=open]:animate-none data-[state=closed]:animate-none"
          style={{ background: 'transparent', boxShadow: 'none', zIndex: 50 }}
        >
          {panel}
        </PopoverContent>
      </>
    );
  }

  // Released: same panel, portaled to body so env-banner translateZ(0) cannot
  // retarget `fixed` (dev.app.getruta.com always shows that banner). Phone
  // width insets both edges; desktop flex-end floats it without `right` + drag.
  if (typeof document === 'undefined') return receivedModal;
  return (
    <>
      {receivedModal}
      {createPortal(
        <div ref={constraintsRef} className={detachedRutaAiShellClass(fillsViewport)}>
          {panel}
        </div>,
        document.body,
      )}
    </>
  );
}
